import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, GitPullRequest, X } from 'lucide-react';

import {
  decideRevisionRequest,
  describeChainStep,
  listRevisionRequests,
  submitRevisionRequest,
  withdrawRevisionRequest,
  type RevisionRequest,
} from '@/api/sops.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';

/**
 * Revision requests on one SOP (ADR-041).
 *
 * Visible to every reader: anyone can propose a change, and the history of who
 * asked for what is part of the SOP's story. Decide buttons appear only for
 * the person the CURRENT step resolves to — the server is the authority, and
 * a 403 here means the chain moved on, not that the UI is broken.
 */

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  open: 'info',
  in_review: 'warning',
  approved: 'success',
  fulfilled: 'success',
  rejected: 'danger',
  withdrawn: 'neutral',
};

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function RevisionRequestsCard({
  slug,
  myMembershipId,
}: {
  slug: string;
  myMembershipId: string | null;
}) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [composing, setComposing] = useState(false);

  const requestsQuery = useQuery({
    queryKey: ['sop-revision-requests', slug],
    queryFn: () => listRevisionRequests(slug),
    enabled: Boolean(slug),
  });

  const decideMutation = useMutation({
    mutationFn: (args: { id: string; decision: 'approved' | 'rejected'; comment?: string }) =>
      decideRevisionRequest(args.id, {
        decision: args.decision,
        comment: args.comment ?? null,
      }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['sop-revision-requests', slug] });
      await qc.invalidateQueries({ queryKey: ['sop', slug] });
      show(
        res.status === 'fulfilled'
          ? 'Approved — a draft revision has been opened.'
          : `Request ${res.status.replace('_', ' ')}.`,
        { variant: 'success' },
      );
    },
    onError: () => {
      show('Could not record that decision. Someone may have already acted on it.', {
        variant: 'danger',
      });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (id: string) => withdrawRevisionRequest(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sop-revision-requests', slug] });
      show('Request withdrawn.', { variant: 'success' });
    },
    onError: () => {
      show('Could not withdraw the request.', { variant: 'danger' });
    },
  });

  const requests = requestsQuery.data ?? [];

  return (
    <Card className="space-y-3 p-5 print:hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-kw-shark">
            <GitPullRequest className="size-4 text-kw-cerulean-dark" /> Revision requests
          </h2>
          <p className="mt-1 max-w-prose text-[13px] text-kw-muted">
            Spotted something that needs changing? Propose it here — it routes through your org’s approval
            chain, and an approved request opens a draft revision automatically.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setComposing(true);
          }}
        >
          Request a revision
        </Button>
      </div>

      {requests.length === 0 ? (
        <p className="text-[13px] text-kw-muted">No revision requests yet.</p>
      ) : (
        <ul className="divide-y divide-kw-border">
          {requests.map((req) => (
            <RequestRow
              key={req.id}
              request={req}
              isRequester={req.requestedByMembershipId === myMembershipId}
              onDecide={(decision, comment) => {
                decideMutation.mutate({ id: req.id, decision, ...(comment ? { comment } : {}) });
              }}
              onWithdraw={() => {
                withdrawMutation.mutate(req.id);
              }}
              busy={decideMutation.isPending || withdrawMutation.isPending}
            />
          ))}
        </ul>
      )}

      {composing ? (
        <ComposeModal
          slug={slug}
          onClose={() => {
            setComposing(false);
          }}
        />
      ) : null}
    </Card>
  );
}

function RequestRow({
  request,
  isRequester,
  onDecide,
  onWithdraw,
  busy,
}: {
  request: RevisionRequest;
  isRequester: boolean;
  onDecide: (decision: 'approved' | 'rejected', comment?: string) => void;
  onWithdraw: () => void;
  busy: boolean;
}) {
  const [comment, setComment] = useState('');
  const pending = request.status === 'open' || request.status === 'in_review';
  const step = request.steps[request.currentStepIndex];

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-[14px] font-medium text-kw-shark">{request.summary}</span>
        <Badge variant={STATUS_VARIANT[request.status] ?? 'neutral'}>{titleCase(request.status)}</Badge>
        <span className="text-[12px] text-kw-muted">
          by {request.requesterNameSnapshot} · {new Date(request.createdAt).toLocaleDateString()}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-[13px] text-kw-muted">{request.body}</p>

      {pending && step ? (
        <p className="text-[12px] text-kw-ghost">
          Waiting on: {describeChainStep(step)} (step {request.currentStepIndex + 1} of {request.steps.length}
          )
        </p>
      ) : null}

      {request.decisions.length > 0 ? (
        <ul className="space-y-0.5">
          {request.decisions.map((d, i) => (
            <li key={`${d.decidedAt}-${String(i)}`} className="text-[12px] text-kw-muted">
              {d.decision === 'approved' ? '✓' : '✕'} {d.approverNameSnapshot} — {titleCase(d.decision)}
              {d.comment ? `: ${d.comment}` : ''}
            </li>
          ))}
        </ul>
      ) : null}

      {request.openedVersionId ? (
        <p className="text-kw-success-text text-[12px]">A draft revision was opened for this request.</p>
      ) : null}

      {pending ? (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {/* Decide controls are shown to everyone; the server is the authority
              on who may actually act, and returns 403 otherwise. Hiding them
              client-side would need a second copy of the chain logic. */}
          <Input
            className="max-w-xs"
            placeholder="Comment (optional)"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
            }}
          />
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => {
              onDecide('approved', comment.trim() || undefined);
            }}
          >
            <Check className="size-4" /> Approve
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              onDecide('rejected', comment.trim() || undefined);
            }}
          >
            <X className="size-4" /> Reject
          </Button>
          {isRequester ? (
            <button
              type="button"
              disabled={busy}
              onClick={onWithdraw}
              className="ml-auto text-[12px] text-kw-muted hover:text-kw-danger-text"
            >
              Withdraw
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function ComposeModal({ slug, onClose }: { slug: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');

  const mutation = useMutation({
    mutationFn: () => submitRevisionRequest(slug, { summary: summary.trim(), body: body.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sop-revision-requests', slug] });
      show('Revision request submitted — your reviewer has been notified.', { variant: 'success' });
      onClose();
    },
    onError: () => {
      show('Could not submit the request.', { variant: 'danger' });
    },
  });

  const canSubmit = summary.trim().length > 0 && body.trim().length > 0 && !mutation.isPending;

  return (
    <Modal open onClose={onClose} title="Request a revision">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mutation.mutate();
        }}
      >
        <p className="text-[13px] text-kw-muted">
          Describe what should change and why. This goes to your org’s approval chain — it does not edit the
          SOP directly.
        </p>
        <Input
          label="Summary"
          required
          placeholder="e.g. Step 3 doesn’t say who releases the hold"
          value={summary}
          onChange={(e) => {
            setSummary(e.target.value);
          }}
        />
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Details</span>
          <textarea
            required
            rows={5}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
            }}
            placeholder="What is wrong or missing, and what should it say instead?"
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!canSubmit}>
            Submit request
          </Button>
        </div>
      </form>
    </Modal>
  );
}
